'use client';

import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, RotateCcw, Download, FileText } from 'lucide-react';

type Item = {
  id: string;
  name: string;
  value: number; // 1-10
  effort: number; // 1-10
};

type QuadrantKey = 'quickWin' | 'bigBet' | 'fillIn' | 'avoid';

const QUADRANTS: Record<
  QuadrantKey,
  { label: string; sub: string; pillClass: string; dotClass: string }
> = {
  quickWin: {
    label: 'Quick Wins',
    sub: 'High value, low effort — do first',
    pillClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
    dotClass: 'fill-emerald-500',
  },
  bigBet: {
    label: 'Big Bets',
    sub: 'High value, high effort — plan carefully',
    pillClass: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
    dotClass: 'fill-sky-500',
  },
  fillIn: {
    label: 'Fill-Ins',
    sub: 'Low value, low effort — spare capacity',
    pillClass: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
    dotClass: 'fill-amber-500',
  },
  avoid: {
    label: 'Money Pits',
    sub: 'Low value, high effort — deprioritize',
    pillClass: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
    dotClass: 'fill-red-500',
  },
};

const MID = 5.5; // 1-10 scale midpoint

function quadrantFor(value: number, effort: number): QuadrantKey {
  const highValue = value >= MID;
  const lowEffort = effort < MID;
  if (highValue && lowEffort) return 'quickWin';
  if (highValue && !lowEffort) return 'bigBet';
  if (!highValue && lowEffort) return 'fillIn';
  return 'avoid';
}

// Simple value/effort ratio for ranking within "do first" order.
function priorityScore(item: Item): number {
  return item.value / item.effort;
}

const STORAGE_KEY = 'planetroadmap.value-effort-matrix.v1';

function newItem(name = '', value = 5, effort = 5): Item {
  return { id: Math.random().toString(36).slice(2), name, value, effort };
}

const SEED: Item[] = [
  newItem('In-app onboarding checklist', 8, 3),
  newItem('SSO with Google', 7, 4),
  newItem('Public API v2', 9, 9),
  newItem('Dark mode', 4, 3),
  newItem('On-prem deployment', 5, 9),
  newItem('CSV import', 6, 2),
];

export function ValueEffortMatrix() {
  const [items, setItems] = useState<Item[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Item[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setItems(parsed);
          setHydrated(true);
          return;
        }
      }
    } catch {}
    setItems(SEED);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [items, hydrated]);

  function update(id: string, patch: Partial<Item>) {
    setItems((curr) => curr.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }
  function remove(id: string) {
    setItems((curr) => curr.filter((i) => i.id !== id));
  }
  function add() {
    setItems((curr) => [...curr, newItem(`Feature ${curr.length + 1}`)]);
  }
  function reset() {
    if (!confirm('Clear all features and start over?')) return;
    setItems([]);
  }

  const ranked = useMemo(
    () =>
      [...items]
        .map((i, idx) => ({ ...i, idx, q: quadrantFor(i.value, i.effort), score: priorityScore(i) }))
        .sort((a, b) => {
          const order: QuadrantKey[] = ['quickWin', 'bigBet', 'fillIn', 'avoid'];
          const qd = order.indexOf(a.q) - order.indexOf(b.q);
          if (qd !== 0) return qd;
          return b.score - a.score;
        }),
    [items]
  );

  function exportCsv() {
    const rows = [
      ['Feature', 'Value (1-10)', 'Effort (1-10)', 'Quadrant', 'Value/Effort'],
      ...ranked.map((i) => [
        i.name || 'Untitled',
        String(i.value),
        String(i.effort),
        QUADRANTS[i.q].label,
        i.score.toFixed(2),
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    download(csv, 'value-effort-matrix.csv', 'text/csv');
  }

  function exportMarkdown() {
    const lines: string[] = ['# Value vs Effort Matrix', ''];
    const order: QuadrantKey[] = ['quickWin', 'bigBet', 'fillIn', 'avoid'];
    for (const q of order) {
      const qItems = ranked.filter((i) => i.q === q);
      if (qItems.length === 0) continue;
      lines.push(`## ${QUADRANTS[q].label}`, '');
      for (const item of qItems) {
        lines.push(`- **${item.name || 'Untitled'}** — value ${item.value}, effort ${item.effort}`);
      }
      lines.push('');
    }
    download(lines.join('\n'), 'value-effort-matrix.md', 'text/markdown');
  }

  function download(content: string, filename: string, type: string) {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading tool…
      </div>
    );
  }

  // Plot geometry
  const W = 520;
  const H = 520;
  const PAD = 36;
  const plotW = W - PAD * 2;
  const plotH = H - PAD * 2;
  const x = (effort: number) => PAD + ((effort - 1) / 9) * plotW; // effort: left=low, right=high
  const y = (value: number) => PAD + (1 - (value - 1) / 9) * plotH; // value: top=high

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      {/* Plot */}
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-5">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-auto"
          role="img"
          aria-label="Value versus effort scatter plot of your features"
        >
          {/* Quadrant backgrounds */}
          <rect x={PAD} y={PAD} width={plotW / 2} height={plotH / 2} className="fill-emerald-500/5" />
          <rect x={PAD + plotW / 2} y={PAD} width={plotW / 2} height={plotH / 2} className="fill-sky-500/5" />
          <rect x={PAD} y={PAD + plotH / 2} width={plotW / 2} height={plotH / 2} className="fill-amber-500/5" />
          <rect x={PAD + plotW / 2} y={PAD + plotH / 2} width={plotW / 2} height={plotH / 2} className="fill-red-500/5" />

          {/* Mid axes */}
          <line x1={PAD + plotW / 2} y1={PAD} x2={PAD + plotW / 2} y2={PAD + plotH} className="stroke-border" strokeDasharray="4 4" />
          <line x1={PAD} y1={PAD + plotH / 2} x2={PAD + plotW} y2={PAD + plotH / 2} className="stroke-border" strokeDasharray="4 4" />

          {/* Frame */}
          <rect x={PAD} y={PAD} width={plotW} height={plotH} className="fill-none stroke-border" />

          {/* Quadrant labels */}
          <text x={PAD + plotW / 4} y={PAD + 16} textAnchor="middle" className="fill-emerald-600 dark:fill-emerald-400 text-[11px] font-semibold">Quick Wins</text>
          <text x={PAD + (plotW * 3) / 4} y={PAD + 16} textAnchor="middle" className="fill-sky-600 dark:fill-sky-400 text-[11px] font-semibold">Big Bets</text>
          <text x={PAD + plotW / 4} y={PAD + plotH - 8} textAnchor="middle" className="fill-amber-600 dark:fill-amber-400 text-[11px] font-semibold">Fill-Ins</text>
          <text x={PAD + (plotW * 3) / 4} y={PAD + plotH - 8} textAnchor="middle" className="fill-red-600 dark:fill-red-400 text-[11px] font-semibold">Money Pits</text>

          {/* Axis captions */}
          <text x={PAD + plotW / 2} y={H - 6} textAnchor="middle" className="fill-muted-foreground text-[11px]">Effort →</text>
          <text x={12} y={PAD + plotH / 2} textAnchor="middle" transform={`rotate(-90 12 ${PAD + plotH / 2})`} className="fill-muted-foreground text-[11px]">Value →</text>

          {/* Points */}
          {items.map((it) => {
            const q = quadrantFor(it.value, it.effort);
            return (
              <g key={it.id}>
                <circle cx={x(it.effort)} cy={y(it.value)} r={9} className={`${QUADRANTS[q].dotClass} opacity-80`} />
                <text x={x(it.effort)} y={y(it.value) - 13} textAnchor="middle" className="fill-foreground text-[10px] font-medium">
                  {(it.name || 'Untitled').slice(0, 18)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Controls + ranked list */}
      <div className="space-y-4">
        <div className="space-y-3">
          {items.map((item) => {
            const q = quadrantFor(item.value, item.effort);
            return (
              <div key={item.id} className="bg-white dark:bg-card border border-border/60 rounded-xl p-4 group">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => update(item.id, { name: e.target.value })}
                    placeholder="Feature name"
                    className="flex-1 bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-1 py-0.5 text-sm font-medium"
                  />
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${QUADRANTS[q].pillClass}`}>
                    {QUADRANTS[q].label}
                  </span>
                  <button
                    onClick={() => remove(item.id)}
                    aria-label="Remove feature"
                    className="text-muted-foreground/60 hover:text-red-500 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <label className="block">
                    <span className="text-xs text-muted-foreground flex justify-between">
                      <span>Value</span>
                      <span className="font-semibold text-foreground">{item.value}</span>
                    </span>
                    <input
                      type="range"
                      min={1}
                      max={10}
                      value={item.value}
                      onChange={(e) => update(item.id, { value: Number(e.target.value) })}
                      className="w-full accent-emerald-500"
                      aria-label={`Value for ${item.name || 'feature'}`}
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground flex justify-between">
                      <span>Effort</span>
                      <span className="font-semibold text-foreground">{item.effort}</span>
                    </span>
                    <input
                      type="range"
                      min={1}
                      max={10}
                      value={item.effort}
                      onChange={(e) => update(item.id, { effort: Number(e.target.value) })}
                      className="w-full accent-sky-500"
                      aria-label={`Effort for ${item.name || 'feature'}`}
                    />
                  </label>
                </div>
              </div>
            );
          })}
          <button
            onClick={add}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-border/60 px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add feature
          </button>
        </div>

        <div className="flex flex-wrap gap-3 justify-end items-center pt-1">
          <button
            onClick={exportCsv}
            disabled={items.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={exportMarkdown}
            disabled={items.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50 transition-colors"
          >
            <FileText className="w-4 h-4" />
            Export Markdown
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
        <p className="text-xs text-muted-foreground text-right">
          Saved automatically in your browser. Nothing leaves this page.
        </p>
      </div>
    </div>
  );
}
